import { useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { useNavigate, matchPath, useLocation } from "react-router-dom";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Typography,
} from "@material-ui/core";

// Tilted location pin icon (map-marker style)
const pinFill = "mdi:map-marker";

// ─── Empty placeholder ────────────────────────────────────────────────────────

function EmptyPlaceholder() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2.5,
        px: 2,
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "1.5px dashed rgba(238,75,43,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(238,75,43,0.5)",
        }}
      >
        <Icon icon={pinFill} width={18} height={18} />
      </Box>

      <Typography
        sx={{ fontWeight: 700, fontSize: "11.5px", color: "text.secondary" }}
      >
        Nothing pinned yet
      </Typography>

      <Typography
        sx={{
          fontSize: "10.5px",
          color: "text.disabled",
          textAlign: "center",
          lineHeight: 1.5,
          maxWidth: 155,
        }}
      >
        Hover any menu item and click the pin icon to add it here
      </Typography>
    </Box>
  );
}

// ─── Single pinned item ───────────────────────────────────────────────────────

PinnedItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  onUnpin: PropTypes.func.isRequired,
};

function PinnedItem({ title, path, isActive, onUnpin }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  return (
    <ListItemButton
      disableGutters
      onClick={() => navigate(path)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        height: 40,
        pl: 2,
        pr: 1,
        position: "relative",
        color: isActive ? "primary.main" : "text.secondary",
        bgcolor: isActive ? "rgba(238,75,43,0.08)" : "transparent",
        "&:hover": { bgcolor: "rgba(238,75,43,0.06)" },
        "&:before": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: 3,
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
          bgcolor: "primary.main",
          display: isActive ? "block" : "none",
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 28 }}>
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            bgcolor: isActive ? "primary.main" : "rgba(238,75,43,0.35)",
            transform: isActive ? "scale(1.5)" : "scale(1)",
          }}
        />
      </ListItemIcon>

      <ListItemText
        disableTypography
        primary={title}
        sx={{
          fontSize: "13px",
          fontWeight: isActive ? 600 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      />

      <Tooltip title="Unpin" placement="right">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onUnpin(path);
          }}
          sx={{
            p: 0.4,
            ml: 0.5,
            flexShrink: 0,
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.15s",
            color: "text.disabled",
            "&:hover": { color: "error.main" },
          }}
        >
          <Icon icon={closeCircleFill} width={14} height={14} />
        </IconButton>
      </Tooltip>
    </ListItemButton>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

PinnedNavSection.propTypes = {
  pinnedItems: PropTypes.array.isRequired,
  isShow: PropTypes.bool,
  onUnpin: PropTypes.func.isRequired,
};

export default function PinnedNavSection({ pinnedItems, isShow = true, onUnpin }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const match = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  // Collapsed sidebar
  if (!isShow) {
    if (pinnedItems.length === 0) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, mt: 1.5 }}>
        <Box sx={{ color: "rgba(238,75,43,0.5)", lineHeight: 0 }}>
          <Icon icon={pinFill} width={14} height={14} />
        </Box>
        {pinnedItems.map(({ title, path }) => (
          <Tooltip key={path} title={title} placement="right">
            <Box
              onClick={() => navigate(path)}
              sx={{
                cursor: "pointer",
                width: 34,
                height: 34,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: match(path) ? "rgba(238,75,43,0.12)" : "transparent",
                "&:hover": { bgcolor: "rgba(238,75,43,0.08)" },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: match(path) ? "primary.main" : "rgba(238,75,43,0.4)",
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
    );
  }

  // Expanded sidebar
  return (
    <Box
      sx={{
        mx: 1.5,
        mt: 1.5,
        borderRadius: "10px",
        border: "1.5px solid rgba(238,75,43,0.25)",
        overflow: "hidden",
        bgcolor: "rgba(238,75,43,0.03)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 1,
          bgcolor: "rgba(238,75,43,0.09)",
          borderBottom: "1px solid rgba(238,75,43,0.15)",
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "6px",
            bgcolor: "rgba(238,75,43,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            flexShrink: 0,
          }}
        >
          <Icon icon={pinFill} width={12} height={12} />
        </Box>

        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 800,
            letterSpacing: "0.9px",
            textTransform: "uppercase",
            color: "primary.main",
            flex: 1,
            lineHeight: 1,
          }}
        >
          Pinned
        </Typography>

        {pinnedItems.length > 0 && (
          <Box
            sx={{
              minWidth: 20,
              height: 20,
              borderRadius: "6px",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.75,
            }}
          >
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>
              {pinnedItems.length}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Items or empty state */}
      {pinnedItems.length === 0 ? (
        <EmptyPlaceholder />
      ) : (
        <List disablePadding>
          {pinnedItems.map(({ title, path }) => (
            <PinnedItem
              key={path}
              title={title}
              path={path}
              isActive={match(path)}
              onUnpin={onUnpin}
            />
          ))}
        </List>
      )}
    </Box>
  );
}
